/**
 * Safely extracts error information from any thrown value.
 * JavaScript catch blocks can receive anything — not just Error instances.
 */
export function extractErrorInfo(error: unknown): {
	name: string;
	message: string;
	stack: string | undefined;
} {
	if (error instanceof Error) {
		return {
			name: error.name || 'Error',
			message: error.message || '(empty message)',
			stack: error.stack,
		};
	}

	if (typeof error === 'string') {
		return {
			name: 'StringError',
			message: error,
			stack: undefined,
		};
	}

	if (error === null || error === undefined) {
		return {
			name: 'NullishError',
			message: `Thrown value was ${String(error)}`,
			stack: undefined,
		};
	}

	// Objects with message-like properties (e.g. API error responses)
	if (typeof error === 'object') {
		const obj = error as Record<string, unknown>;
		const name = typeof obj.name === 'string' ? obj.name : 'ObjectError';
		const message =
			typeof obj.message === 'string' ? obj.message : typeof obj.error === 'string' ? obj.error : safeStringify(error);
		const stack = typeof obj.stack === 'string' ? obj.stack : undefined;

		return { name, message, stack };
	}

	// Remaining types (number, boolean, bigint, symbol, function) are all safe to stringify
	return {
		name: 'UnknownError',
		message: `${error as number}`,
		stack: undefined,
	};
}

function safeStringify(value: unknown): string {
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
}
